import React, { useContext } from 'react';
import { LabeledItem } from '@core/components';
import { XData } from '@core/utils';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import { SimpleFormContext } from '../SimpleForm';
import { handleArrayVal } from './handleArrayVal';
import {
  WithLabelWrappedProps,
  ComponentClosedValue,
  TFormItemPropsWChildren,
  WithPopupFormValueProps,
  RecordValue,
  ValChangedCbParams
} from './types';

const withLabel = <TFormItemProps,>(
  WrappedComponent: React.FC<
    ComponentClosedValue<TFormItemProps> & WithLabelWrappedProps
  >
) => (props: ComponentClosedValue<TFormItemProps> & WithLabelWrappedProps) => {
  const { labelText, required, hideLabel, reversed } = props;

  return (
    <LabeledItem
      labelText={labelText}
      hideLabel={hideLabel}
      required={required}
      reversed={reversed}
    >
      <WrappedComponent {...props} />
    </LabeledItem>
  );
};

const withPopupFormValue = <TFormItemProps,>(
  WrappedComponent: React.FC<WithPopupFormValueProps & TFormItemProps>,
  defaultProps?: TFormItemProps
) => (
  props: WithPopupFormValueProps & TFormItemPropsWChildren<TFormItemProps>
) => {
  const { fieldName, relativeFields, valueExpr, children, required } = props;

  const { setFormItemValue, getFormItemValue } = useContext(SimpleFormContext);

  const prepareRelativeFields = (relativeRecord: RecordValue) => {
    if (!relativeFields) return;

    relativeFields.forEach(({ local, relative }) => {
      setFormItemValue(local, relativeRecord[relative]);
    });
  };

  const formItemValue = getFormItemValue(fieldName);

  const handleValueChanged = ({ value }: ValChangedCbParams) => {
    if (XData.isArray<RecordValue>(value)) {
      handleArrayVal({
        value,
        valueExpr,
        formItemValue,
        getFormItemValue,
        setFormItemValue,
        fieldName,
        relativeFields: relativeFields || []
      });

      return;
    }

    if (XData.isObjLiteral<RecordValue>(value)) {
      setFormItemValue(fieldName, value[valueExpr || 'Id']);
      prepareRelativeFields(value);

      return;
    }

    if (formItemValue === value) {
      return;
    }

    setFormItemValue(fieldName, value);
    prepareRelativeFields({});
  };

  const onValueChange = (data: any) => {
    if (!props.onValueChange) {
      return;
    }

    props.onValueChange(data, { setFormItemValue, getFormItemValue });
  };

  return (
    <WrappedComponent
      {...defaultProps}
      {...props}
      valueExpr={relativeFields ? undefined : valueExpr}
      value={formItemValue}
      onValueChanged={handleValueChanged}
      onValueChange={onValueChange}
    >
      {children || required ? (
        <Validator>
          {required && <RequiredRule />}
          {children}
        </Validator>
      ) : null}
    </WrappedComponent>
  );
};

export const formItemNoLabelHocSetup = <TFormItemProps,>(
  FormItemComponent: unknown,
  defaultProps?: TFormItemProps
) => {
  const WPopupFormVal = withPopupFormValue<
    ComponentClosedValue<TFormItemPropsWChildren<TFormItemProps>>
  >(
    FormItemComponent as (
      props: ComponentClosedValue<TFormItemPropsWChildren<TFormItemProps>>
    ) => JSX.Element,
    defaultProps as ComponentClosedValue<
      TFormItemPropsWChildren<TFormItemProps>
    >
  );

  return WPopupFormVal;
};

export const formItemHocSetup = <TFormItemProps,>(
  FormItemComponent: unknown,
  defaultProps?: TFormItemProps
) => {
  const WPopupFormVal = formItemNoLabelHocSetup(
    FormItemComponent,
    defaultProps
  );

  return withLabel<TFormItemPropsWChildren<TFormItemProps>>(WPopupFormVal);
};
